import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import BrandSelect from 'src/components/brand-select';
import { useStores } from 'src/hooks/use-stores';
import {
  GqlBrand,
  GqlCustomNavigationItem,
  CustomNavigationItemType,
  useBrandsForDropdownByIdsQuery,
} from 'types/graphql';

import { ModalControllerProps } from './modal-controller';
import { BaseCustomNavigationModal } from './base-custom-navigation-modal';

type BrandOption = {
  key: string;
  label: string;
  value: GqlBrand;
};

export function AddBrandModal({ editingItem, onClose, onSave }: ModalControllerProps): JSX.Element {
  const { apolloClient } = useStores();
  const [selectedBrand, setSelectedBrand] = useState<BrandOption | null>(null);
  const [label, setLabel] = useState(editingItem?.label ?? '');

  const connectedId = editingItem?.connectedId;

  // When editing an existing brand navigation item, fetch the brand so the
  // "Select a brand" field is pre-populated instead of appearing empty.
  const { data: existingBrandData } = useBrandsForDropdownByIdsQuery({
    client: apolloClient,
    skip: !connectedId,
    variables: { ids: [connectedId ?? ''] },
  });

  // Guards against clobbering a selection the user has already made (including
  // explicitly clearing it) once the initial connected brand has been applied.
  const hasAppliedInitialBrand = useRef(false);

  useEffect(() => {
    if (hasAppliedInitialBrand.current) {
      return;
    }

    const existingBrand = existingBrandData?.getBrandsV2?.[0];
    if (existingBrand) {
      hasAppliedInitialBrand.current = true;
      setSelectedBrand({
        key: existingBrand._id,
        label: existingBrand.name,
        value: existingBrand as GqlBrand,
      });
    }
  }, [existingBrandData]);

  const isEditing = !!editingItem;
  const isValid = (!!selectedBrand?.value.id || isEditing) && label.trim() !== '';

  const handleChangeBrand = (brandOption: BrandOption | null): void => {
    hasAppliedInitialBrand.current = true;
    setSelectedBrand(brandOption);
    if (brandOption && !label) {
      setLabel(brandOption.label);
    }
  };

  const handleSave = (): void => {
    if (!isValid) {
      return;
    }

    const item: GqlCustomNavigationItem = {
      id: editingItem?.id ?? uuid(),
      items: editingItem?.items ?? [],
      label: label.trim(),
      navigationType: CustomNavigationItemType.brand,
      connectedId: selectedBrand?.value.id ?? editingItem?.connectedId,
      position: editingItem?.position ?? 0,
    };

    onSave(item);
    onClose();
  };

  return (
    <BaseCustomNavigationModal
      copy='Connect to a brand page.'
      handleSave={handleSave}
      headerText='Brand'
      inputLabel={label}
      isEditing={isEditing}
      isOpen
      isValid={isValid}
      onClose={onClose}
      setLabel={setLabel}
    >
      <BrandSelect brand={selectedBrand?.value} onChange={handleChangeBrand} placeholder='Select a brand' width={348} />
    </BaseCustomNavigationModal>
  );
}
